import { Component, Input, OnInit } from "@angular/core";
import { RewardsService } from "../../../../../services/rewards/rewards.service";
import {
  TransactionDisplay,
  TransactionResponse,
  TransactionType,
} from "../../../../../services/rewards/rewards.interface";
import { LoadingService } from "../../../../../services/loading/loading.service";
import { numberWithCommas } from "../../../../../services/utils/number.utils";

@Component({
  selector: "rc-transaction-table",
  templateUrl: "./transaction-table.component.html",
  styleUrls: ["./transaction-table.component.scss"],
  standalone: false,
})
export class TransactionTableComponent implements OnInit {
  @Input() public userId: string;
  @Input() public maxTransactions: number = 10;

  public transactions: TransactionDisplay[] = [];
  public currentPage: number = 0;
  public totalTransactions: number = 0;
  public hasNextPage: boolean = false;
  public hasPreviousPage: boolean = false;

  constructor(
    private readonly rewardsService: RewardsService,
    private readonly loadingService: LoadingService,
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.loadTransactions();
  }

  public async loadTransactions(): Promise<void> {
    await this.loadingService.wrapInLoading(async () => {
      const offset = this.currentPage * this.maxTransactions;
      const response = await this.rewardsService.getTransactionsForUser(this.userId, this.maxTransactions, offset);

      this.transactions = response.data.map((txn) => this.enrichTransaction(txn));
      this.totalTransactions = response.total;
      this.updatePaginationState();
    });
  }

  public async nextPage(): Promise<void> {
    if (this.hasNextPage) {
      this.currentPage++;
      await this.loadTransactions();
    }
  }

  public async previousPage(): Promise<void> {
    if (this.hasPreviousPage) {
      this.currentPage--;
      await this.loadTransactions();
    }
  }

  private enrichTransaction(txn: TransactionResponse): TransactionDisplay {
    const pointsAmount = parseInt(txn.pointsAmount, 10);
    const isPositive = pointsAmount > 0;
    const type = this.determineTransactionType(txn);

    return {
      ...txn,
      type,
      isPositive,
      formattedAmount: `${isPositive ? "+" : ""}${numberWithCommas(Math.abs(pointsAmount))}`,
    };
  }

  private determineTransactionType(txn: TransactionResponse): TransactionType {
    if (txn.isStripeOriginated) {
      const pointsAmount = parseInt(txn.pointsAmount, 10);
      return pointsAmount > 0 ? TransactionType.STRIPE_PAYMENT : TransactionType.STRIPE_REFUND;
    } else {
      const pointsAmount = parseInt(txn.pointsAmount, 10);
      return pointsAmount > 0 ? TransactionType.ADMIN_ADD : TransactionType.ADMIN_DEDUCT;
    }
  }

  private updatePaginationState(): void {
    this.hasPreviousPage = this.currentPage > 0;
    this.hasNextPage = (this.currentPage + 1) * this.maxTransactions < this.totalTransactions;
  }

  public getTransactionIcon(transaction: TransactionDisplay): string {
    return transaction.isStripeOriginated ? "card" : "person";
  }

  public getTransactionColor(transaction: TransactionDisplay): string {
    return transaction.isPositive ? "success" : "danger";
  }

  // Expose Math to template
  public readonly Math = Math;
}
